import { useState, useCallback, useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { OPEN_EOD_SESSION } from '../../graphql/production-mutations'
import { GET_DAILY_LEDGER_RECIPES, GET_DAILY_LEDGER_FINANCIAL, GET_TRACKED_RECIPES, GET_EOD_SESSION } from '../../graphql/production-queries'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import LoadingSpinner from '../../components/LoadingSpinner'
import SessionHeader from './components/SessionHeader'
import DaySummaryBar from './components/DaySummaryBar'
import StaffInstructions from './components/StaffInstructions'
import Stepper, { type StepConfig } from './components/Stepper'
import SessionSummaryCard from './components/SessionSummaryCard'
import CookedStep from './components/steps/CookedStep'
import ReheatedStep from './components/steps/ReheatedStep'
import SoldPosStep from './components/steps/SoldPosStep'
import StoredStep from './components/steps/StoredStep'
import WastedExpiredStep from './components/steps/WastedExpiredStep'
import StaffConsumptionStep from './components/steps/StaffConsumptionStep'
import IncidentsNotesStep from './components/steps/IncidentsNotesStep'
import SummaryStep from './components/steps/SummaryStep'

function todayString() {
  return new Date().toISOString().slice(0, 10)
}

export const STEPS: StepConfig[] = [
  { id: 'cooked',           label: 'Cooked',              shortLabel: 'Cooked' },
  { id: 'reheated',         label: 'Reheated',            shortLabel: 'Reheated' },
  { id: 'pos-sales',        label: 'Sold via POS',        shortLabel: 'POS Sales' },
  { id: 'stored',           label: 'Stored in fridge',    shortLabel: 'Stored' },
  { id: 'wasted',           label: 'Food Wastage',         shortLabel: 'Wastage' },
  { id: 'staff',            label: 'Staff consumption',   shortLabel: 'Staff' },
  { id: 'incidents-notes',  label: 'Incidents & notes',   shortLabel: 'Notes' },
  { id: 'summary',          label: 'Summary',             shortLabel: 'Summary' },
]

const EndOfShiftPage = () => {
  const { hasPermission } = usePermissions()
  const canLog = hasPermission(PERMISSIONS.PRODUCTION_LOG)
  const canViewMargins = hasPermission(PERMISSIONS.COSTING_MARGIN_VIEW)

  const [date, setDate] = useState(todayString)
  const [currentStep, setCurrentStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())
  const [instructionsOpen, setInstructionsOpen] = useState(false)
  const [session, setSession] = useState<any>(null)
  const [sessionLoading, setSessionLoading] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)

  const [openEodSession] = useMutation(OPEN_EOD_SESSION)

  // Fallback query for shiftleads who can view (PRODUCTION_VIEW) but cannot create sessions
  const { data: sessionQueryData } = useQuery(GET_EOD_SESSION, {
    variables: { date },
    skip: !sessionError,
    fetchPolicy: 'network-only',
  })

  const loadSession = useCallback(async (d: string) => {
    setSessionLoading(true)
    setSessionError(null)
    try {
      const { data } = await openEodSession({ variables: { date: d } })
      setSession(data.openEodSession)
    } catch (e: any) {
      setSessionError(e.message ?? 'Failed to open session')
    } finally {
      setSessionLoading(false)
    }
  }, [openEodSession])

  useEffect(() => {
    loadSession(date)
    setCurrentStep(0)
    setCompletedSteps(new Set())
  }, [date, loadSession])

  const {
    data: ledgerData,
    loading: ledgerLoading,
    error: ledgerError,
    refetch: refetchLedger,
  } = useQuery(GET_DAILY_LEDGER_RECIPES, {
    variables: { date },
    fetchPolicy: 'network-only',
  })

  const { data: financialData } = useQuery(GET_DAILY_LEDGER_FINANCIAL, {
    variables: { date },
    fetchPolicy: 'network-only',
    skip: !canViewMargins,
  })

  // Fetch yesterday's ledger to prefill reheated quantities
  const yesterday = useMemo(() => {
    const dt = new Date(date)
    dt.setDate(dt.getDate() - 1)
    return dt.toISOString().slice(0, 10)
  }, [date])

  const { data: yesterdayData } = useQuery(GET_DAILY_LEDGER_RECIPES, {
    variables: { date: yesterday },
    fetchPolicy: 'cache-first',
  })

  const storedYesterday = useMemo(() => {
    const map: Record<number, number> = {}
    for (const r of yesterdayData?.dailyLedgerSummary?.recipes ?? []) {
      if (r.stored_quantity > 0) map[r.raw_material_id] = r.stored_quantity
    }
    return map
  }, [yesterdayData])

  const { data: trackedRecipesData } = useQuery(GET_TRACKED_RECIPES, {
    fetchPolicy: 'cache-first',
  })

  const outputQtyMap = useMemo(() => {
    const map: Record<number, number | null> = {}
    for (const r of trackedRecipesData?.trackedRecipes ?? []) {
      map[r.id] = r.output_quantity ?? null
    }
    return map
  }, [trackedRecipesData])

  const handleSessionUpdate = (updated: any) => {
    setSession((prev: any) => ({ ...prev, ...updated }))
  }

  const markStepDone = (stepIndex: number) => {
    setCompletedSteps((prev) => new Set(prev).add(stepIndex))
  }

  const goNext = () => {
    markStepDone(currentStep)
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1)
  }

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1)
  }

  const handleStepClick = (index: number) => setCurrentStep(index)

  // When openEodSession is not permitted for this role, fall back to the read-only query result
  const resolvedSession = session ?? sessionQueryData?.eodSession ?? null

  const financialSummary = financialData?.dailyLedgerSummary ?? null
  const recipes = useMemo(() => {
    const rows = ledgerData?.dailyLedgerSummary?.recipes ?? []
    return rows.map((r: any) => ({
      ...r,
      output_quantity: outputQtyMap[r.raw_material_id] ?? null,
    }))
  }, [ledgerData, outputQtyMap])

  const isSubmitted = resolvedSession?.status === 'submitted'

  const stepProps = {
    recipes,
    date,
    canLog,
    onLogged: refetchLedger,
    onNext: goNext,
    onBack: goBack,
    isFirst: currentStep === 0,
  }

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case 'cooked':          return <CookedStep {...stepProps} />
      case 'reheated':        return <ReheatedStep {...stepProps} storedYesterday={storedYesterday} />
      case 'pos-sales':       return <SoldPosStep {...stepProps} />
      case 'stored':          return <StoredStep {...stepProps} />
      case 'wasted':          return <WastedExpiredStep {...stepProps} />
      case 'staff':           return <StaffConsumptionStep date={date} onNext={goNext} onBack={goBack} isFirst={currentStep === 0} />
      case 'incidents-notes':
        return resolvedSession ? (
          <IncidentsNotesStep
            {...stepProps}
            session={resolvedSession}
            onSessionUpdate={handleSessionUpdate}
          />
        ) : null
      case 'summary':
        return (
          <SummaryStep
            recipes={recipes}
            session={resolvedSession}
            canLog={canLog}
            onBack={goBack}
            onSessionUpdate={handleSessionUpdate}
            storedYesterday={storedYesterday}
          />
        )
      default: return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">End of Shift</h1>
          <p className="mt-1 text-sm text-gray-500">
            Log what happened in the kitchen today. POS sales are filled in automatically.
          </p>
        </div>
        <button
          onClick={() => setInstructionsOpen(!instructionsOpen)}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
        >
          <InformationCircleIcon className="h-5 w-5" />
          {instructionsOpen ? 'Hide instructions' : 'Instructions'}
        </button>
      </div>

      {instructionsOpen && <StaffInstructions />}

      {sessionError && !resolvedSession && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{sessionError}</div>
      )}

      <SessionHeader
        date={date}
        session={resolvedSession}
        sessionLoading={sessionLoading}
        canLog={canLog}
        onDateChange={(d) => setDate(d)}
        onSessionUpdate={handleSessionUpdate}
      />

      {financialSummary && canViewMargins && <DaySummaryBar summary={financialSummary} />}

      {ledgerLoading || sessionLoading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : ledgerError ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{ledgerError.message}</div>
      ) : isSubmitted ? (
        /* Submitted — show read-only summary card */
        <SessionSummaryCard
          session={resolvedSession!}
          recipes={recipes}
        />
      ) : (
        /* Draft — show stepper */
        resolvedSession && (
          <div className="space-y-6">
            <Stepper
              steps={STEPS}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={handleStepClick}
            />
            <div className="card p-6">
              {renderStep()}
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default EndOfShiftPage
